/**
 * Evaluates portal wire `visibleIf` strings against live answers.
 * Supports SurveyJS-style "{questionName} = rhs" and "cond1 and cond2".
 */
export function evaluateWireVisibleIf(
  visibleIf: string | undefined | null,
  answers: Record<string, unknown>
): boolean {
  if (visibleIf == null || !String(visibleIf).trim()) return true;

  const parts = String(visibleIf)
    .trim()
    .split(/\s+and\s+/i)
    .map((p) => p.trim())
    .filter(Boolean);

  return parts.every((part) => evaluateWireVisibleIfClause(part, answers));
}

function lookupAnswer(ref: string, answers: Record<string, unknown>): unknown {
  if (Object.prototype.hasOwnProperty.call(answers, ref)) return answers[ref];
  const lower = ref.toLowerCase();
  for (const [k, v] of Object.entries(answers)) {
    if (k.toLowerCase() === lower) return v;
  }
  return undefined;
}

function evaluateWireVisibleIfClause(clause: string, answers: Record<string, unknown>): boolean {
  const m = clause.match(/^\{([^}]+)\}\s*=\s*(.+)$/i);
  if (!m) return false;

  const name = m[1].trim();
  let rhs = m[2].trim();
  const answer = lookupAnswer(name, answers);

  if (/^["']/.test(rhs) && rhs.length >= 2 && rhs.endsWith(rhs[0])) {
    const expected = rhs.slice(1, -1);
    return String(answer) === expected;
  }

  const lower = rhs.toLowerCase();
  if (lower === "true") return answer === true;
  if (lower === "false") return answer === false;
  if (lower === "yes") return answer === true || answer === "yes" || answer === "Yes";
  if (lower === "no") return answer === false || answer === "no" || answer === "No";

  if (lower === "empty" || lower === "notempty") {
    const empty = answer == null || answer === "" || (Array.isArray(answer) && answer.length === 0);
    return lower === "empty" ? empty : !empty;
  }

  return String(answer) === rhs;
}
